import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { usePopupElement } from '@shared/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { OrgLevel } from '@retail/auth/types';
import { OrgUnitAssortment, OrgUnitType } from '@retail/mgpris/config-types';
import { OrgUnitAssortmentHeading } from './OrgUnitAssortmentHeading';
import { OrgUnitAssortmentSearchHeader } from './OrgUnitAssortmentSearchHeader';
import { useOrgUnitTypeLabel } from './useOrgUnitTypeLabel';

const sectionLevels: OrgLevel[] = ['BRAND', 'DATAOWNER', 'LOCATION'];

const searchThreshold = 10;

interface Props {
  assortments: OrgUnitAssortment[];
  selectedAssortment: OrgUnitAssortment;
  selectAssortment: (assortmentId: number) => void;
}

export const OrgUnitAssortmentSelect: React.FC<Props> = ({
  assortments,
  selectedAssortment,
  selectAssortment,
}) => {
  const t = useAppTFunction();
  const typeLabel = useOrgUnitTypeLabel();
  const { menuElementProps, triggerElementProps } = usePopupElement();

  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<OrgUnitType[]>([]);
  const [expandedLocations, setExpandedLocations] = useState<number[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<OrgLevel[]>([]);

  const sectionTitle = useCallback(
    (level: OrgLevel) => {
      switch (level) {
        case 'BRAND':
          return t('app.orgUnit.brand.singular');
        case 'DATAOWNER':
          return t('app.orgUnit.masterAssortment');
        default:
          return t('app.orgUnit.department.plural');
      }
    },
    [t]
  );

  const normalizedQuery = query.trim().toLowerCase();
  const isFiltering = !!normalizedQuery || selectedTypes.length > 0;

  const typeOptions = useMemo(
    () =>
      OrgUnitType.values().filter((type) =>
        assortments.some(({ orgUnitType }) => orgUnitType === type)
      ),
    [assortments]
  );

  const sections = useMemo(() => {
    const matches = assortments.filter(
      ({ name, orgUnitType }) =>
        (!normalizedQuery || name?.toLowerCase().includes(normalizedQuery)) &&
        (selectedTypes.length === 0 || selectedTypes.includes(orgUnitType))
    );

    return sectionLevels
      .map((level) => ({
        level,
        assortments: matches.filter(({ assortmentLevel }) => assortmentLevel === level),
      }))
      .filter((section) => section.assortments.length > 0);
  }, [assortments, normalizedQuery, selectedTypes]);

  const collapsibleLevels: OrgLevel[] = useMemo(() => {
    const countAt = (level: OrgLevel) =>
      assortments.filter(({ assortmentLevel }) => assortmentLevel === level).length;

    return countAt('DATAOWNER') > 1 && countAt('LOCATION') > 1 ? ['DATAOWNER', 'LOCATION'] : [];
  }, [assortments]);

  const toggleType = useCallback(
    (orgUnitType: OrgUnitType) =>
      setSelectedTypes((selected) =>
        selected.includes(orgUnitType)
          ? selected.filter((type) => type !== orgUnitType)
          : [...selected, orgUnitType]
      ),
    []
  );

  const toggleSection = useCallback(
    (level: OrgLevel) =>
      setCollapsedSections((collapsed) =>
        collapsed.includes(level)
          ? collapsed.filter((collapsedLevel) => collapsedLevel !== level)
          : [...collapsed, level]
      ),
    []
  );

  const toggleLocation = useCallback(
    (assortmentId: number) =>
      setExpandedLocations((expanded) =>
        expanded.includes(assortmentId)
          ? expanded.filter((id) => id !== assortmentId)
          : [...expanded, assortmentId]
      ),
    []
  );

  return (
    <Fragment>
      <OrgUnitAssortmentHeading
        name={selectedAssortment.name}
        sx={{ cursor: 'pointer' }}
        endIcon={
          <ChevronLeft
            sx={(theme) => ({
              transform: menuElementProps.open ? 'rotate(90deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease-in-out',
              color: 'common.black',
              ...theme.applyStyles('dark', { color: 'text.primary' }),
            })}
          />
        }
        {...triggerElementProps}
      />
      <Menu
        {...menuElementProps}
        elevation={1}
        slotProps={{
          paper: { sx: { width: '415px' } },
          transition: {
            onEntered: () => {
              // Deferred so focus does not fire inside React's commit phase
              setTimeout(() => searchInputRef.current?.focus());
            },
          },
        }}
      >
        {assortments.length > searchThreshold && (
          <OrgUnitAssortmentSearchHeader
            query={query}
            setQuery={setQuery}
            inputRef={searchInputRef}
            typeOptions={typeOptions}
            selectedTypes={selectedTypes}
            toggleType={toggleType}
            clearTypes={() => setSelectedTypes([])}
          />
        )}
        {sections.length === 0 && (
          <Typography color="text.secondary" px={2} py={1.5}>
            {t('app.orgUnit.noMatches')}
          </Typography>
        )}
        {sections.map(({ level, assortments: sectionAssortments }) => {
          const isCollapsible = collapsibleLevels.includes(level);

          const isSectionExpanded =
            !isCollapsible || isFiltering || !collapsedSections.includes(level);

          return (
            <Box key={level} pt={2}>
              <ListSubheader
                onClick={isCollapsible ? () => toggleSection(level) : undefined}
                sx={{
                  cursor: isCollapsible ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {sectionTitle(level)}
                {isCollapsible && (
                  <ChevronRight
                    sx={{
                      transform: isSectionExpanded ? 'rotate(90deg)' : null,
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  />
                )}
              </ListSubheader>
              {isSectionExpanded &&
                sectionAssortments.map((assortment) => {
                  const priceLists = assortment.priceLists ?? [];
                  const isExpanded = expandedLocations.includes(assortment.assortmentId);

                  return (
                    <Stack key={assortment.assortmentId}>
                      <MenuItem
                        value={assortment.assortmentId}
                        onClick={() => selectAssortment(assortment.assortmentId)}
                        selected={assortment.assortmentId === selectedAssortment.assortmentId}
                        sx={{ py: priceLists.length > 0 ? 0.5 : 1 }}
                      >
                        <Stack
                          flexDirection="row"
                          flexGrow={1}
                          gap={1}
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack>
                            <Typography>{assortment.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {typeLabel(assortment.orgUnitType)}
                            </Typography>
                          </Stack>
                          {priceLists.length > 0 && (
                            <Tooltip title={t('app.orgUnit.priceList.singular')}>
                              <div>
                                <IconButton
                                  onClick={(e) => {
                                    toggleLocation(assortment.assortmentId);
                                    e.stopPropagation();
                                  }}
                                  sx={{ zIndex: 1 }}
                                >
                                  <ChevronRight
                                    sx={{
                                      transform: isExpanded ? 'rotate(90deg)' : null,
                                      transition: 'transform 0.2s ease-in-out',
                                    }}
                                  />
                                </IconButton>
                              </div>
                            </Tooltip>
                          )}
                        </Stack>
                      </MenuItem>
                      {isExpanded && (
                        <Box pt={2}>
                          <ListSubheader sx={{ pl: 6 }}>
                            {t('app.orgUnit.priceList.singular')}
                          </ListSubheader>
                          {priceLists.map((priceList) => (
                            <MenuItem
                              key={priceList.assortmentId}
                              sx={{
                                pl: 6,
                                bgcolor: (theme) =>
                                  `${theme.vars.palette.background.paper} !important`,
                                ':hover': {
                                  bgcolor: (theme) => `${theme.vars.palette.divider} !important`,
                                },
                              }}
                              value={priceList.assortmentId}
                              onClick={() => selectAssortment(priceList.assortmentId)}
                              selected={priceList.assortmentId === selectedAssortment.assortmentId}
                            >
                              {priceList.name}
                            </MenuItem>
                          ))}
                        </Box>
                      )}
                    </Stack>
                  );
                })}
            </Box>
          );
        })}
      </Menu>
    </Fragment>
  );
};
